import { Icon } from '@/components/Icon';
import { track } from '@/lib/analytics';
import { haptic, openExternalLink } from '@/lib/telegram';
import { Italic } from './parts';

interface SetupOtherDeviceProps {
  subscriptionUrl: string;
  onCopy: () => void;
}

interface Platform {
  id: 'ios' | 'android' | 'mac' | 'win';
  name: string;
  glyph: string;
  glyphItalic: boolean;
  note: string;
  download: string;
}

const PLATFORMS: Platform[] = [
  {
    id: 'ios',
    name: 'iOS',
    glyph: 'iOS',
    glyphItalic: true,
    note: 'iphone, ipad',
    download: 'https://apps.apple.com/app/happ-proxy-utility/id6504287215',
  },
  {
    id: 'android',
    name: 'Android',
    glyph: '◉',
    glyphItalic: false,
    note: 'смартфоны и планшеты',
    download: 'https://play.google.com/store/apps/details?id=com.happproxy',
  },
  {
    id: 'mac',
    name: 'macOS',
    glyph: 'mac',
    glyphItalic: true,
    note: 'macbook, imac',
    download: 'https://www.happ.su/main',
  },
  {
    id: 'win',
    name: 'Windows',
    glyph: '⊞',
    glyphItalic: false,
    note: 'pc, ноутбуки',
    download: 'https://www.happ.su/main',
  },
];

function stripProtocol(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

export function SetupOtherDevice({ subscriptionUrl, onCopy }: SetupOtherDeviceProps) {
  const hasUrl = Boolean(subscriptionUrl);

  const handleCopy = async () => {
    if (!hasUrl) return;
    haptic('medium');
    let copied = false;
    try {
      await navigator.clipboard.writeText(subscriptionUrl);
      copied = true;
    } catch {
      const ta = document.createElement('textarea');
      ta.value = subscriptionUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        copied = document.execCommand('copy');
      } catch {
        /* both paths failed — sandboxed WebView */
      }
      document.body.removeChild(ta);
    }
    if (copied) onCopy();
  };

  return (
    <div
      className="no-scrollbar"
      style={{ padding: '12px 18px 130px', overflowY: 'auto', height: '100%' }}
    >
      <div style={{ marginTop: 8, marginBottom: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          установка · другое устройство
        </div>
        <h1
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontWeight: 400,
            fontSize: 38,
            lineHeight: 0.98,
            margin: 0,
            color: 'var(--ink)',
            letterSpacing: '-0.02em',
          }}
        >
          выбери
          <br />
          <Italic>платформу.</Italic>
        </h1>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 12, lineHeight: 1.5 }}>
          один ключ работает на всех твоих устройствах. покажем, что нажимать на каждой ос
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 10,
          marginBottom: 22,
        }}
      >
        {PLATFORMS.map((p) => (
          <button
            key={p.id}
            onClick={() => {
              haptic('light');
              track('miniapp_happ_link_opened', { surface: 'other_device', platform: p.id });
              openExternalLink(p.download);
            }}
            style={{
              appearance: 'none',
              cursor: 'pointer',
              background: '#fbf8f1',
              border: '1px solid var(--paper-3)',
              borderRadius: 18,
              padding: '20px 16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 10,
              textAlign: 'left',
              minHeight: 130,
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 10,
                background: 'var(--paper)',
                border: '1px solid var(--paper-3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'Instrument Serif', serif",
                fontSize: p.glyph.length > 1 ? 16 : 22,
                color: 'var(--ink)',
                fontStyle: p.glyphItalic ? 'italic' : 'normal',
              }}
            >
              {p.glyph}
            </div>
            <div style={{ flex: 1 }}>
              <div className="eyebrow" style={{ marginBottom: 4 }}>
                инструкция
              </div>
              <div style={{ fontSize: 16, color: 'var(--ink)', fontWeight: 500 }}>
                для {p.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>{p.note}</div>
            </div>
          </button>
        ))}
      </div>

      <div
        style={{
          background: 'var(--ink)',
          color: 'var(--paper)',
          borderRadius: 18,
          padding: '16px 18px',
        }}
      >
        <div
          className="eyebrow"
          style={{ color: 'rgba(244,240,232,0.55)', marginBottom: 10 }}
        >
          твоя ссылка на подписку
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <code
            style={{
              flex: 1,
              fontFamily: 'var(--f-mono)',
              fontSize: 12,
              color: hasUrl ? 'var(--paper)' : 'rgba(244,240,232,0.4)',
              letterSpacing: '0.01em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {hasUrl ? stripProtocol(subscriptionUrl) : 'оформи подписку — появится ссылка'}
          </code>
          <button
            onClick={handleCopy}
            disabled={!hasUrl}
            aria-label="скопировать ссылку"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'rgba(244,240,232,0.12)',
              border: 'none',
              cursor: hasUrl ? 'pointer' : 'not-allowed',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: hasUrl ? 1 : 0.45,
            }}
          >
            <Icon name="copy" size={15} stroke="var(--paper)" sw={1.8} />
          </button>
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'rgba(244,240,232,0.5)',
            marginTop: 10,
            lineHeight: 1.5,
          }}
        >
          одна ссылка — все устройства. вставь в happ / hiddify / v2raytun на любой платформе
        </div>
      </div>
    </div>
  );
}
